import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, Chip, Button, Grid, Paper, List, ListItem, ListItemText } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { StatChip } from './StatChip';
import { ExpandedRowContent, DetailSection } from './ExpandedRowContent';
import { RecipientsModal } from './RecipientsModal';
import { fetchCampaignDetails } from 'api/emailHistory/campaignsApi';
import { formatDateWithTime } from 'utils/emailHistory/dateUtils';
import { FunnelChart, DeliveryStatusPieChart } from './charts';
import { useIsMobile } from 'hooks/useIsMobile';

/**
 * @component ExpandedRowCampaign
 * @description Expanded row content for campaign details
 * @param {Object} props
 * @param {number} props.campaignId - Campaign ID
 * @param {function} props.onNavigateToBulkEmails - Navigation handler
 * @returns {JSX.Element}
 */
export function ExpandedRowCampaign({ campaignId, onNavigateToBulkEmails }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);

  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchCampaignDetails(campaignId);
        setDetails(data);
      } catch (err) {
        console.error('Error fetching campaign details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [campaignId]);

  // Calculate bounce rate
  const bounceRate = details?.totalEmailsSent > 0
    ? ((details.bounced / details.totalEmailsSent) * 100).toFixed(2)
    : 0;
  const isMobile = useIsMobile();

  return (
    <>
      <ExpandedRowContent
        loading={loading}
        error={error}
        isEmpty={!details}
        showEmailPreview={false}
      >
        {/* Performance Metrics Stats */}
        <DetailSection title="Performance Metrics">
          {isMobile ? (
            <Box sx={{ py: 1.5 }}>
              <List dense sx={{ py: 0 }}>
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <ListItemText 
                    primary="Delivery Rate" 
                    secondary={`${details?.deliveryRate}%`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'body1', fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <ListItemText 
                    primary="Bounce Rate" 
                    secondary={`${bounceRate}%`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'body1', fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <ListItemText 
                    primary="Open Rate" 
                    secondary={`${details?.openRate}%`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'body1', fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <ListItemText 
                    primary="Click Rate" 
                    secondary={`${details?.clickRate}%`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'body1', fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <ListItemText 
                    primary="Click/Open" 
                    secondary={`${details?.clickToOpenRate}%`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'body1', fontWeight: 600 }}
                  />
                </ListItem>
              </List>
            </Box>
          ) : (
            <Box sx={{ py: 1.5 }}>
              <Stack 
                direction="row" 
                spacing={2} 
                flexWrap="wrap" 
                sx={{ 
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                <StatChip label="Delivery Rate" value={`${details?.deliveryRate}%`} />
                <StatChip label="Bounce Rate" value={`${bounceRate}%`} />
                <StatChip label="Open Rate" value={`${details?.openRate}%`} />
                <StatChip label="Click Rate" value={`${details?.clickRate}%`} />
                <StatChip label="Click/Open" value={`${details?.clickToOpenRate}%`} />
              </Stack>
            </Box>
          )}
        </DetailSection>

        {/* Graphs in 2-column layout */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Left Column: Funnel Chart */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Campaign Performance
              </Typography>
              <FunnelChart
                sent={details?.totalEmailsSent || 0}
                delivered={details?.delivered || 0}
                opened={details?.uniqueOpens || 0}
                clicked={details?.uniqueClicks || 0}
                loading={loading}
                height={250}
              />
            </Paper>
          </Grid>

          {/* Right Column: Pie Chart and Volume Chart */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              {/* Delivery Status Pie Chart */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  Delivery Status
                </Typography>
                <DeliveryStatusPieChart
                  delivered={details?.delivered || 0}
                  bounced={(details?.hardBounced || 0) + (details?.softBounced || 0)}
                  failed={0} // Failed count not available in API response
                  loading={loading}
                  width={300}
                  height={200}
                />
                {/* Bounce Breakdown */}
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {details?.hardBounced > 0 && (
                      <Chip
                        label={`Hard: ${details.hardBounced}`}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    )}
                    {details?.softBounced > 0 && (
                      <Chip
                        label={`Soft: ${details.softBounced}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                    {(details?.hardBounced || 0) === 0 && (details?.softBounced || 0) === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No bounces
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        {/* Summary Info */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Last Activity
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Last Send: {formatDateWithTime(details?.lastSendDate)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
            justifyContent: isMobile ? 'stretch' : 'space-between', 
            mt: 3 
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setRecipientsModalOpen(true)}
            fullWidth={isMobile}
          >
            View Recipients
          </Button>
          <Button
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            onClick={() => onNavigateToBulkEmails(campaignId)}
            fullWidth={isMobile}
          >
            View Bulk Emails in this Campaign
          </Button>
        </Box>
      </ExpandedRowContent>

      {/* Recipients Modal */}
      <RecipientsModal
        open={recipientsModalOpen}
        onClose={() => setRecipientsModalOpen(false)}
        listId={campaignId}
      />
    </>
  );
}

